<template>
  <a-form
    ref="form"
    :model="formState"
    :label-col="{ span: 7 }"
    :rules="formRules"
    :label-align="'left'"
  >
    <a-form-item label="表盘名称" name="name" required>
      <a-input
        ref="nameInputRef"
        v-model:value="formState.name"
        class=""
        :disabled="disabledKeys.includes('name')"
      />
    </a-form-item>
    <a-form-item label="表盘类型" name="shape" required>
      <a-form-item-rest>
        <div class="flex items-center">
          <a-select
            v-model:value="formState.shape"
            :bordered="false"
            :disabled="disabledKeys.includes('shape')"
            class="pt-[2px]"
            @change="onPickShape"
          >
            <a-select-option v-for="option in deviceShapes" :key="option.value">
              <icon
                :icon="`${option.value}.svg`"
                :width="20"
                :height="20"
                fill="none"
                class="-mt-[3px]"
              />
              <span class="ml-1">{{ option.label }}</span>
            </a-select-option>
          </a-select>
        </div>
      </a-form-item-rest>
    </a-form-item>

    <!-- 设备型号或产品名称 -->
    <a-form-item
      :label="
        GlobalConfig.showDeviceTypeProductionName ? '设备名称' : '设备型号'
      "
      name="deviceType"
    >
      <div class="flex items-center">
        <a-select
          v-model:value="formState.deviceType"
          class="flex-1 h-10 pt-[2px]"
          :disabled="disabledKeys.includes('deviceType')"
          :bordered="false"
        >
          <a-select-option
            v-for="option in wholeDevices
              .filter((item) => {
                // 判断当前是编辑模式并且表盘属于表盘样例中的表盘，此时需要过滤非464*464尺寸的表盘
                if (mode === 'complex' && formState.deviceType && samples.includes(formState.deviceType) && formState.size === '464x464') {
                  return item.size === formState.size
                }
                return item.shape == formState.shape;
              })
              .sort((a, b) => {
                return a.deviceType.localeCompare(b.deviceType);
              })"
            :key="option.deviceType"
          >
            <template
              v-if="
                GlobalConfig.showDeviceTypeProductionName && option.productName
              "
            >
              {{ option.productName }}
            </template>

            <template v-else>
              {{ option.deviceType }}
              <span
                class="ml-1"
                :class="{
                  'text-[#a3a3a3]': !disabledKeys.includes('deviceType')
                }"
              >
                {{ option.size }}
              </span>
            </template>
          </a-select-option>
        </a-select>
      </div>
    </a-form-item>

    <!-- 提示 -->
    <div v-if="showIntranetWarning" class="text-yellow-200">
      未连接到小米内网，部分设备可能无法使用，请先连接到小米内网。
    </div>

    <!-- 智能类型 -->
    <a-form-item v-if="isAndroid" label="智能类型" name="watchOS">
      <a-checkbox-group
        v-model:value="watchOSChecked"
        :disabled="disabledKeys.includes('watchOS')"
        @change="onWatchOSChange"
      >
        <a-checkbox
          v-for="item in watchOSOptions"
          :key="item.value"
          :value="item.value"
          :disabled="item.disabled"
          >{{ item.label }}</a-checkbox
        >
      </a-checkbox-group>
    </a-form-item>

    <template v-if="mode == 'complex'">
      <a-form-item :colon="false" class="mb-2 !w-full">
        <template #label>
          <div
            class="w-full flex items-center"
            @click="() => (showHighSetting = !showHighSetting)"
          >
            <Icon
              icon="arrow-down.svg"
              :width="15"
              :height="15"
              fill="none"
              class="transition-all"
              :class="{
                'rotate-180': !!showHighSetting
              }"
            />
            <span class="pl-2 pt-[2px]" style="color: rgb(163, 163, 163)">
              高级设置
            </span>
          </div>
        </template>
      </a-form-item>
      <div v-show="showHighSetting" class="transition-all">
        <a-form-item label="版本号" name="version">
          <a-input
            v-model:value="formState.version"
            class="bg-transparent"
            :disabled="disabledKeys.includes('version')"
          >
          </a-input>
        </a-form-item>
        <a-form-item label="商户ID" name="merchantId" v-if="isNormal">
          <a-input
            v-model:value="formState.merchantId"
            class="bg-transparent"
            :disabled="disabledKeys.includes('merchantId')"
          >
          </a-input>
        </a-form-item>
        <a-form-item label="设计师" name="author">
          <a-input
            v-model:value="formState.author"
            class="bg-transparent"
            :disabled="disabledKeys.includes('author')"
          >
          </a-input>
        </a-form-item>
        <a-form-item v-if="watchfaceIdEditable" label="表盘ID" name="pkgName">
          <a-input
            v-model:value="formState.pkgName"
            class="bg-transparent"
            :disabled="disabledKeys.includes('pkgName')"
          >
          </a-input>
        </a-form-item>

        <template v-if="mode == 'complex' || !canUseGetPath">
          <a-form-item label="保存目录" name="dir">
            <a-input
              v-model:value="formState.dir"
              class="dir"
              :disabled="disabledKeys.includes('dir')"
              readonly
              @click="onPickDir"
            >
              <template #suffix>
                <icon
                  icon="fileDir.svg"
                  :width="20"
                  :height="20"
                  fill="transparent"
                  @click="onPickDir"
                />
              </template>
            </a-input>
          </a-form-item>
        </template>
      </div>
    </template>

    <template v-if="mode == 'complex' && isNormal">
      <a-form-item :colon="false" class="mb-2 !w-full">
        <template #label>
          <div
            class="w-full flex items-center"
            @click="() => (showIntroduction = !showIntroduction)"
          >
            <Icon
              icon="arrow-down.svg"
              :width="15"
              :height="15"
              fill="none"
              class="transition-all"
              :class="{
                'rotate-180': !!showIntroduction
              }"
            />
            <span class="pl-2 pt-[2px]" style="color: rgb(163, 163, 163)">
              表盘简介
            </span>
          </div>
        </template>
      </a-form-item>
      <div v-show="showIntroduction" class="transition-all">
        <a-form-item label="版权信息" name="copyright">
          <a-input
            v-model:value="formState.copyright"
            class="bg-transparent"
            :disabled="disabledKeys.includes('copyright')"
          >
          </a-input>
        </a-form-item>

        <a-form-item label="表盘设计" name="designType">
          <a-radio-group
            v-model:value="formState.designType"
            :disabled="disabledKeys.includes('designType')"
          >
            <a-radio
              v-for="item in designTypeOptions"
              :key="item.value"
              :value="item.value"
              >{{ item.label }}</a-radio
            >
          </a-radio-group>
        </a-form-item>
        <a-form-item label="设计亮点" name="highlight">
          <a-input
            v-model:value="formState.highlight"
            class="bg-transparent"
            :disabled="disabledKeys.includes('highlight')"
          >
          </a-input>
        </a-form-item>
        <a-form-item label="规则说明" name="rule">
          <a-input
            v-model:value="formState.rule"
            class="bg-transparent"
            :disabled="disabledKeys.includes('rule')"
          >
          </a-input>
        </a-form-item>
        <div v-if="!!formState.dataSourceList?.length">
          <a-form-item label="数据展示" name="dataSourceList" />
          <a-form-item
            v-for="(item, index) in formState.dataSourceList"
            :name="['dataSourceList', index, 'desc']"
            :rules="[
              {
                validator: () => {
                  return validFormSubItemRule(
                    `${item.label}${item.desc ? `（${item.desc}）` : ''}`
                  );
                }
              }
            ]"
          >
            <div class="flex items-center">
              <span class="shrink-0 mr-3">{{ item.label }}</span>
              <a-input
                v-model:value="item.desc"
                class="bg-transparent"
                :disabled="disabledKeys.includes('dataSourceList')"
              />
            </div>
          </a-form-item>
        </div>
        <div v-if="!!formState.jumpAppList?.length">
          <a-form-item label="跳转功能" name="jumpAppList" />

          <a-form-item
            v-for="(item, index) in formState.jumpAppList"
            :name="['jumpAppList', index, 'desc']"
            :rules="[
              {
                validator: () => {
                  return validFormSubItemRule(
                    `${item.label}${item.desc ? `（${item.desc}）` : ''}`
                  );
                }
              }
            ]"
          >
            <div class="flex items-center">
              <span class="shrink-0 mr-3">{{ item.label }}</span>
              <a-input
                v-model:value="item.desc"
                class="bg-transparent"
                :disabled="disabledKeys.includes('jumpAppList')"
              />
            </div>
          </a-form-item>
        </div>
        <a-form-item label="备注" name="remark">
          <a-input
            v-model:value="formState.remark"
            class="bg-transparent"
            :disabled="disabledKeys.includes('remark')"
          >
          </a-input>
        </a-form-item>
      </div>
    </template>
  </a-form>
  <slot />
  <a-modal
    v-model:visible="previewVisible"
    title="表盘简介预览"
    @ok="() => (previewVisible = false)"
    :z-index="1002"
  >
    <p class="whitespace-pre-wrap">{{ getIntroduce() }}</p>
  </a-modal>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { pickDir } from '@/utils/fs/pick-file.utils';
import {
  WatchfaceFormData,
  jumpAppList,
  useIsIntranet,
  useWatchfaceDevices,
  useWatchfaceManage
} from '@/hooks';
import { ElectronApi } from '@/services/electron.api';
import { WatchfaceService } from '@/services/watchface';
import { FormExpose } from 'ant-design-vue/lib/form/Form';
import { PropType } from 'vue';
import { cloneDeep, isEqual } from 'lodash-es';
import {
  validWatchfaceName,
  validWatchfaceVersion,
  validWatchfaceInfoLength
} from '@/utils/watchface-valid.utils';
import {
  getWatchfaceDefaultId,
  getWatchfaceRandomId
} from '@/utils/watchface-id.utils';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';
import { Description, Introduce } from '@/types/watchface';
import { dataSourceList } from '@/resources/data-source';
import { Shape, ShapeType } from '@/services/shape';
import { useLayerStore } from '@/store/use-layer-store';

const props = defineProps({
  disabledKeys: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  mode: {
    type: String as PropType<'simple' | 'complex'>,
    default: 'complex'
  },
  samples: {
    type: Array as PropType<string[]>,
    default: () => []
  }
});
const { wholeDevices } = useWatchfaceDevices();
const layerStore = useLayerStore();

interface MyForm extends HTMLFormElement, FormExpose {}
interface IntroItemList
  extends Array<{ key: string; label: string; desc: string }> {}
const form = ref<MyForm | null>(null);

// 是否是外部版本（表单item不同）
const isNormal = ref<boolean>(ElectronApi.isNormal());

const formState: Partial<WatchfaceFormData> = reactive({});
const formState2 = ref<Partial<WatchfaceFormData>>({});

const formRules = {
  name: [
    {
      validator: async () => {
        const res = validWatchfaceName(
          formState.name ?? '',
          formState.deviceType
        );
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  deviceType: [
    {
      required: true,
      message: '请选择设备'
    }
  ],
  description: [
    {
      max: 20,
      message: '最多20个字符'
    }
  ],
  pkgName: [
    {
      validator: async () => {
        const pkgName = formState.pkgName ?? '';
        if (!pkgName.length) {
          return Promise.reject('请输入表盘ID');
        }
        if (/^[0-9]{1,12}$/g.test(pkgName) == false) {
          return Promise.reject('请输入数字，最多不超过12位');
        }
        // 用户输入时，不可输入默认id值,无论是否启用了自动id
        const _defaultId = getWatchfaceDefaultId();
        if (pkgName == _defaultId && watchfaceIdEditable.value) {
          return Promise.reject('不可设置为' + _defaultId);
        }
        return Promise.resolve();
      }
    }
  ],
  imageCompression: [
    {
      required: true,
      message: '请设置'
    }
  ],
  author: [
    {
      required: true,
      message: '请输入作者'
    },
    {
      validator: async () => {
        const res = validWatchfaceInfoLength(formState.author ?? '', 15);
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  id: [
    {
      required: true,
      message: '请输入表盘ID'
    }
  ],
  dir: [
    {
      required: true,
      message: '请选择保存目录'
    }
  ],
  version: [
    {
      validator: async () => {
        const res = validWatchfaceVersion(formState.version ?? '');
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  copyright: [
    {
      validator: async () => {
        if (!isNormal.value) return Promise.resolve();
        const res = validWatchfaceInfoLength(formState.copyright ?? '', 30);
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  highlight: [
    {
      validator: async () => {
        if (!isNormal.value) return Promise.resolve();
        const res = validWatchfaceInfoLength(formState.highlight ?? '', 120);
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  rule: [
    {
      validator: async () => {
        if (!isNormal.value) return Promise.resolve();
        if (formState.designType === 'paid' && !formState.rule?.length) {
          return Promise.reject('请输入付费表盘使用规则');
        }
        const res = validWatchfaceInfoLength(formState.rule ?? '', 120);
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ],
  remark: [
    {
      validator: async () => {
        if (!isNormal.value) return Promise.resolve();
        const res = validWatchfaceInfoLength(formState.remark ?? '', 30);
        if (typeof res == 'string') return Promise.reject(res);
        return Promise.resolve();
      }
    }
  ]
};

// 校验表单子项
const validFormSubItemRule = (value: string) => {
  const res = validWatchfaceInfoLength(value, 80);
  return new Promise<void>((resolve, reject) => {
    if (typeof res === 'string') {
      reject(res);
    } else {
      resolve();
    }
  });
};

const showHighSetting = ref(true);
const showIntroduction = ref(true);
// 是否可编辑表盘id
const watchfaceIdEditable = ref(false);

// 当修改了设备型号时，需要重置pkgName和version
const { generatePkgName } = useWatchfaceManage();
const _tempState = reactive({
  pkgName: '',
  version: '1.0.0'
});
watch(
  () => formState.deviceType,
  (deviceType, preDeviceType) => {
    // 非编辑模式下，无需处理
    if (!watchfacePath.value || !preDeviceType) return;
    const _deviceType = formState2.value.deviceType;
    if (_deviceType == preDeviceType) {
      // 保存修改之前是原来的设备型号
      _tempState.pkgName = formState.pkgName ?? '';
      _tempState.version = formState.version ?? '';
    }

    // 如果新类型又修改为了preDeviceType
    if (deviceType == _deviceType) {
      // 修改设备型号时，代表新创建一个项目，需要对id重新赋值
      formState.pkgName = _tempState.pkgName;
      formState.version = _tempState.version;
    } else {
      formState.pkgName = generatePkgName(deviceType ?? '');
      formState.version = '1.0.0';
    }

    watchfaceIdEditable.value =
      // 如果设备型号是M66，并且启用了自动id，则不可编辑id
      deviceType == 'M66'
        ? false
        : // 其他情况，沿用原来的逻辑
          !canUse('auto-watchface-id', deviceType).valid ||
          formState.pkgName != getWatchfaceDefaultId();
  }
);

// const watchfaceShapes: ('circle' | 'square' | 'run')[] = [
//   'circle',
//   'square',
//   'run'
// ];

const watchfacePath = ref('');

const canUseGetPath = ref(false);
onMounted(async () => {
  try {
    await ElectronApi.getPath('userData');
    canUseGetPath.value = true;
  } catch (error) {
    canUseGetPath.value = false;
  }
});

const nameInputRef = ref();

const getInitDataSourceList = (initList: IntroItemList) => {
  const flatDataSourceList = Object.entries(dataSourceList)
    .map((i) => i[1])
    .flat();

  const dataSourceKeys = new Set<string>();
  for (const theme of layerStore.themes) {
    const shapes = Shape.shapeTree(theme.id, false);
    const stack = [...shapes];
    while (stack.length) {
      const shape = stack.shift();
      if (!shape) break;
      if (shape?.children.length) stack.push(...shape.children);
      for (const key of shape.soueceKeys) {
        dataSourceKeys.add(key);
      }
    }
  }
  const res: IntroItemList = [];
  dataSourceKeys.forEach((key) => {
    const curDataSource = flatDataSourceList.find(
      (dataSource) => dataSource.key == key
    );
    if (!curDataSource) return;
    const desc = initList.find((item) => item.key == key)?.desc ?? '';
    res.push({ key, label: curDataSource.label, desc });
  });
  return res;
};

const getInitJumpAppList = (initList: IntroItemList) => {
  const jumpAppKeys = new Set<string>();
  for (const theme of layerStore.themes) {
    const shapes = Shape.shapeTree(theme.id, false);
    const stack = [...shapes];
    while (stack.length) {
      const shape = stack.shift();
      if (!shape) break;
      if (shape?.children.length) stack.push(...shape.children);
      if (
        shape.type === ShapeType.Widget &&
        shape.attrs.jumpApp &&
        shape.attrs.jumpApp !== 'none'
      ) {
        jumpAppKeys.add(shape.attrs.jumpApp);
      }
    }
  }
  const res: IntroItemList = [];
  jumpAppKeys.forEach((key) => {
    const curJumpApp = jumpAppList.find((jumpApp) => jumpApp.name == key);
    if (!curJumpApp) return;
    const desc = initList.find((item) => item.key == key)?.desc ?? '';
    res.push({ key, label: curJumpApp.label, desc });
  });
  return res;
};

const initData = async (_path: string) => {
  // if (!_path) throw '请输入_path';

  watchfacePath.value = _path;
  const service = new WatchfaceService(_path);
  const res = _path
    ? await service.description.getDescriptionData()
    : undefined;
  const introRes = _path
    ? (await service.editorConfig.getEditorData())?.introData
    : undefined;

  formState.shape =
    wholeDevices.value.find((item) => item.deviceType == res?.deviceType)
      ?.shape ?? 'circle';

  const defaultDevice = wholeDevices.value.find(
    (item) => item.shape == formState.shape
  );

  formState.name = res?.name ?? '我的表盘';
  formState.deviceType = res?.deviceType ?? defaultDevice?.deviceType;
  formState.version = res?.version ?? '1.0.0';
  formState.size = res?.size ?? defaultDevice?.size;
  formState.author = res?.author ?? '小米';
  // formState.imageCompression = res?.imageCompression ?? false;
  formState.dir = _path
    ? ElectronApi.path?.dirname(_path)
    : (await WatchfaceService.getSaveDir()) ?? '';

  // 表盘id，如果没有则自动添加默认id
  formState.pkgName = res?.pkgName ?? '';

  // 增加智能类型字段
  formState.watchOS = res?.watchOS ?? 'vela';
  watchOSChecked.value =
    formState.watchOS == 'android' ? ['android'] : ['vela', 'android'];

  // 仅外部版本item
  if (isNormal.value) {
    // 商户id
    formState.merchantId = res?.merchantId ?? '';
    // 表盘简介相关字段
    formState.copyright = introRes?.copyright ?? '';
    formState.designType = introRes?.designType ?? 'paid';
    formState.highlight = introRes?.highlight ?? '';
    formState.rule = introRes?.rule ?? '';
    formState.dataSourceList = getInitDataSourceList(
      introRes?.dataSourceList ?? []
    );
    formState.jumpAppList = getInitJumpAppList(introRes?.jumpAppList ?? []);
    formState.remark = introRes?.remark ?? '';
  }

  formState2.value = cloneDeep(formState);

  // 在编辑态时，设置id值 _path存在时，代表编辑态
  const isEditMode = !!_path;
  if (isEditMode) {
    const canUseAutoId = canUse(
      'auto-watchface-id',
      formState.deviceType
    ).valid;
    if (!formState.pkgName) {
      formState.pkgName = canUseAutoId
        ? getWatchfaceDefaultId()
        : getWatchfaceRandomId();
    }
    // 是否允许编辑id
    watchfaceIdEditable.value =
      formState.deviceType == 'M66'
        ? false
        : // 未启用默认id，或者已启用，但是id!=默认id
          !canUseAutoId || formState.pkgName !== getWatchfaceDefaultId();
  }

  // 更正部分字段信息
  if (formState.version == '1.0') formState.version = '1.0.0';
  if (validWatchfaceVersion(formState.version ?? '1.0.0')) {
    let version = formState.version;
    if (/^[\w]{1,}\.[\w]{1,}$/g.test(version)) {
      version += '.0';
    } else if (/^[\w]{1,}$/g.test(version)) {
      version += '.0';
      version += '.0';
    }
    if (version) formState.version = version;
  }

  // 新建工程则选中默认名称
  if (!res?.name) nameInputRef.value.select();
};
const deviceShapes = reactive([
  {
    value: 'circle',
    label: '圆型'
  },
  {
    value: 'square',
    label: '方型'
  },
  {
    value: 'run',
    label: '跑道型'
  }
]);

const onPickShape = (v: string) => {
  formState.deviceType = wholeDevices.value.find(
    (item) => item.shape == v
  )?.deviceType;
};

watch(
  () => formState.deviceType,
  (v) => {
    formState.size = wholeDevices.value.find(
      (item) => item.deviceType == v
    )?.size;
  }
);

const onPickDir = async () => {
  if (props.disabledKeys.includes('dir')) return;
  //替换路径
  const res = await pickDir();
  if (res) {
    formState.dir = res;
    form.value?.validate('dir');
  }
};

const getIntroduce = () => {
  // 内部版本不需要表盘简介
  if (!isNormal.value) {
    return '';
  }

  const {
    author,
    copyright,
    highlight,
    rule,
    dataSourceList = [],
    jumpAppList = [],
    remark
  } = formState;

  // 格式化列表数据
  const formatItems = (items: IntroItemList, prefix = '') =>
    items.length
      ? `${prefix}${items
          .map((item) => `${item.label}${item.desc ? `（${item.desc}）` : ''}`)
          .join('、')}\n`
      : '';

  // 定义内容区块配置
  const sections = [
    { show: copyright, content: `【版权信息】${copyright}\n` },
    { show: highlight || rule, content: '【表盘设计说明】\n' },
    { show: highlight, content: `设计亮点：${highlight}\n` },
    { show: rule, content: `规则说明：${rule}\n` },
    {
      show: dataSourceList.length || jumpAppList.length,
      content: '【功能介绍】\n'
    },
    {
      show: dataSourceList.length,
      content: formatItems(dataSourceList, '1. 数据展示：')
    },
    {
      show: jumpAppList.length,
      content: formatItems(jumpAppList, '2. 跳转应用：')
    },
    {
      show: author,
      content: `【设计师信息】该表盘由外部设计师${author}设计并制作。\n`
    },
    { show: remark, content: `${remark}\n` }
  ];

  // 生成内容并过滤空值
  return `${sections
    .map((section) => (section.show ? section.content : ''))
    .filter(Boolean)
    .join('')}欢迎试用，让你的手腕焕然一新！`;
};

const previewVisible = ref<boolean>(false);

const validFormData = async () => {
  try {
    const names = Object.keys(formRules);
    const dataSourceItemNames =
      formState.dataSourceList?.map((_, index) => [
        'dataSourceList',
        index,
        'desc'
      ]) || [];
    const jumpAppItemNames =
      formState.jumpAppList?.map((_, index) => [
        'jumpAppList',
        index,
        'desc'
      ]) || [];
    await form.value?.validate([
      ...names,
      ...dataSourceItemNames,
      ...jumpAppItemNames
    ]);
    return true;
  } catch (error) {
    return false;
  }
};

// 触发命令
defineExpose({
  getData: async () => {
    const valid = await validFormData();
    const changed = () => {
      const keys1 = Object.keys(formState);
      const keys2 = Object.keys(formState2.value);
      if (keys1.length !== keys2.length) return false;

      const deepKeys = ['dataSourceList', 'jumpAppList'];

      for (let i = 0; i < keys1.length; i++) {
        const key1 = keys1[i] as keyof typeof formState;
        const keyChanged = deepKeys.includes(key1)
          ? !isEqual(formState[key1], formState2.value[key1])
          : formState[key1] !== formState2.value[key1];
        if (keyChanged) {
          return true;
        }
      }

      return false;
    };
    const {
      copyright,
      designType,
      highlight,
      rule,
      dataSourceList,
      jumpAppList,
      remark,
      merchantId,
      ...descriptionData
    } = formState;

    // 不同版本返回不同值
    const data = {
      ...descriptionData,
      merchantId: isNormal.value ? merchantId : '',
      introduce: getIntroduce()
    };
    const introData = isNormal.value
      ? {
          copyright,
          designType,
          highlight,
          rule,
          dataSourceList,
          jumpAppList,
          remark
        }
      : {};

    return {
      valid,
      data,
      introData,
      changed: changed(),
      modifyDevice: formState.deviceType != formState2.value.deviceType
    };
  },
  clear: () => {
    form.value?.clearValidate();
    form.value?.resetFields();
    watchfaceIdEditable.value = false;
  },
  preview: async () => {
    const valid = await validFormData();
    if (!valid) return;
    previewVisible.value = true;
  },
  initData
});

const { findDeivce } = useWatchfaceDevices();

// 表盘智能类型
const watchOSChecked = ref<string[]>([]);
const isAndroid = computed(() => {
  return findDeivce(formState.deviceType ?? 'N62')?.watchOS == 'android';
});
const watchOSOptions = computed(() => {
  const items: {
    label: string;
    value: Description['watchOS'];
    disabled?: boolean;
  }[] = [
    // 默认勾选智能，且必须一直选择，不可取消
    {
      label: '智能',
      value: 'android',
      disabled: true
    },
    {
      label: '轻智能',
      value: 'vela'
    }
  ];

  return items;
});

const designTypeOptions = computed(() => {
  const items: {
    label: string;
    value: Introduce['designType'];
  }[] = [
    // 默认勾选智能，且必须一直选择，不可取消
    {
      label: '付费表盘',
      value: 'paid'
    },
    {
      label: '免费表盘',
      value: 'free'
    }
  ];

  return items;
});

const onWatchOSChange = (v: Description['watchOS'][]) => {
  formState.watchOS = v.includes('vela') ? 'vela' : 'android';
};

// 显示内网提示内容
const { isIntranet } = useIsIntranet();
const showIntranetWarning = computed(() => {
  return (
    ElectronApi.isProfessional() && !isIntranet.value && props.mode == 'simple'
  );
});
</script>

<style scoped lang="less">
:deep(.dir) {
  .ant-input {
    // opacity: 0.7;
    text-decoration: underline;
    text-overflow: ellipsis;
    color: rgba(255, 255, 255, 0.7);
  }
}
:deep(
    .ant-form-item-label
      > label.ant-form-item-required:not(
        .ant-form-item-required-mark-optional
      )::before
  ) {
  content: '';
}

:deep(.ant-form-item-label > label.ant-form-item-required) {
  margin-left: -4px;
}
:deep(.ant-checkbox-disabled + span) {
  color: rgba(255, 255, 255, 1) !important;
}
// :deep(.ant-checkbox-disabled .ant-checkbox-inner) {
//   background-color: rgba(255, 255, 255, 1) !important;
// }
:deep(.ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after) {
  border-color: rgba(0, 0, 0, 0.7) !important;
}
</style>
